<template>
  <el-dialog
    class="record-detail" title="训练记录详情" :visible.sync="visible"
    width="1120px" top="5vh" :close-on-click-modal="false"
    @close="clear"
  >
    <div ref="detailBody" v-loading="loading" class="detail-body" :aria-busy="loading">
      <div v-if="loading" class="detail-state">正在加载训练记录…</div>
      <div v-else-if="error" class="detail-state" role="alert">
        <el-alert :title="error" type="error" :closable="false" show-icon />
        <el-button class="retry-button" size="small" type="primary" @click="load">重新加载</el-button>
      </div>
      <template v-else-if="detail">
        <el-descriptions :column="3" border size="small" class="record-summary">
          <el-descriptions-item label="训练人员">{{ record.userName || transcript.userName || '未记录' }}</el-descriptions-item>
          <el-descriptions-item label="部门">{{ record.deptName || '未记录' }}</el-descriptions-item>
          <el-descriptions-item label="记录编号">{{ record.sessionId }}</el-descriptions-item>
          <el-descriptions-item label="训练场景" :span="3">{{ record.sceneTitle || transcript.sceneTitle || '未记录' }}</el-descriptions-item>
          <el-descriptions-item label="训练模式">{{ { 1: '练习', 2: '考试' }[record.mode] || '未记录' }}</el-descriptions-item>
          <el-descriptions-item label="训练状态">{{ statusLabel(record.status) }}</el-descriptions-item>
          <el-descriptions-item label="训练用时">{{ formatDuration(record.elapsedSec) }}</el-descriptions-item>
          <el-descriptions-item label="总分"><strong class="total-score">{{ scored ? scoreText(totalScore) + ' 分' : '暂无评分' }}</strong></el-descriptions-item>
          <el-descriptions-item label="通关结果">{{ scored ? passLabel : '暂无结果' }}</el-descriptions-item>
          <el-descriptions-item label="通关线">{{ scored && report.passScore != null ? scoreText(report.passScore) + ' 分' : '未记录' }}</el-descriptions-item>
          <el-descriptions-item label="开始时间">{{ formatRecordTime(transcript.startedAt || report.startedAt) }}</el-descriptions-item>
          <el-descriptions-item label="结束时间">{{ formatRecordTime(transcript.endedAt || report.endedAt) }}</el-descriptions-item>
          <el-descriptions-item label="记录时间">{{ formatRecordTime(record.createdAt) }}</el-descriptions-item>
        </el-descriptions>

        <el-alert
          v-if="!scored" class="record-notice" :title="stateNotice"
          :type="report.state === 'failed' ? 'error' : 'info'" :closable="false" show-icon
        />
        <div class="detail-tabs">
          <div class="detail-tab-nav" role="tablist" aria-label="记录详情">
            <button
              type="button" role="tab" :aria-selected="activeTab === 'conversation'"
              :class="{ active: activeTab === 'conversation' }" @click="activeTab = 'conversation'"
            >对话记录（{{ messages.length }}条）</button>
            <button
              type="button" role="tab" :aria-selected="activeTab === 'evaluation'"
              :class="{ active: activeTab === 'evaluation' }" @click="activeTab = 'evaluation'"
            >评分报告</button>
          </div>
          <div v-show="activeTab === 'conversation'" role="tabpanel" aria-label="对话记录">
            <p class="section-hint">按本次训练的对话顺序展示，内容仅供查看。</p>
            <el-alert v-if="referenceMissing" title="关联对话未在本次记录中找到，请查看下方完整记录。" type="info" :closable="false" />
            <div ref="conversation" class="conversation-list">
              <article
                v-for="(message, index) in messages" :key="message.id || index"
                :data-message-id="message.id" class="conversation-message"
                :class="{ 'message-user': message.role === 'user', 'message-highlight': isReferenced(message.id) }"
              >
                <div class="message-meta">
                  <strong>{{ speaker(message.role) }}</strong>
                  <span>第 {{ index + 1 }} 条<span v-if="message.turn != null"> · {{ message.turn === 0 ? '开场' : '第 ' + message.turn + ' 轮' }}</span></span>
                  <time>{{ formatRecordTime(message.createdAt) }}</time>
                </div>
                <p>{{ message.content || '（内容未记录）' }}</p>
              </article>
              <div v-if="!messages.length" class="detail-empty">本次训练暂无保存的对话记录</div>
            </div>
          </div>
          <div v-show="activeTab === 'evaluation'" role="tabpanel" aria-label="评分报告">
            <div v-if="!scored" class="detail-empty">{{ stateNotice }}</div>
            <training-report
              v-else :key="requestSequence" :report="report"
              @conversation="showConversation" @navigate="scrollToTop"
            />
          </div>
        </div>
      </template>
    </div>
    <div slot="footer"><el-button v-if="detail" size="small" :loading="loading" @click="load">刷新详情</el-button><el-button size="small" @click="visible = false">关闭</el-button></div>
  </el-dialog>
</template>

<script>
import recordApi from '@/api/aiTrain/record'
import { formatRecordTime, scoreText, toArray } from '@/utils/ai-train-record'
import TrainingReport from './TrainingReport'

export default {
  name: 'AiTrainRecordDetail',
  components: { TrainingReport },
  data() {
    return { visible: false, loading: false, error: '', sessionId: null, detail: null, requestSequence: 0, activeTab: 'conversation', referencedIds: [] }
  },
  computed: {
    record() { return this.detail ? this.detail.record : {} },
    transcript() { return this.detail ? this.detail.transcript : {} },
    report() { return this.detail ? this.detail.evaluation : {} },
    // 与 App 一致保留已保存的非 system 消息；其他说话人明确标识，不冒充学员。
    messages() { return this.objectRows(this.transcript.messages).filter(message => message.role !== 'system') },
    scored() { return this.report.state === 'done' },
    totalScore() { return this.report.totalScore != null ? this.report.totalScore : this.record.totalScore },
    passLabel() {
      const passed = this.report.passed != null ? this.report.passed : this.record.passed
      return passed === true ? '通关' : passed === false ? '未通关' : '未记录'
    },
    stateNotice() {
      return {
        in_progress: '训练进行中，尚未提交评分；可查看已保存的对话。',
        evaluating: '训练已结束，正在等待评分结果；可稍后刷新详情。',
        failed: this.report.failureReason || '本次评分失败，暂未生成评分结果。可由有权限的管理员重新评分。',
        abandoned: '本次训练已放弃，未生成评分结果；已保存的对话仍可查看。'
      }[this.report.state] || '本次训练暂无评分结果。'
    },
    referenceMissing() { return this.referencedIds.length > 0 && !this.messages.some(message => this.isReferenced(message.id)) }
  },
  beforeDestroy() { this.clear() },
  methods: {
    formatRecordTime,
    scoreText,
    objectRows(value) { return toArray(value).filter(row => row && typeof row === 'object' && !Array.isArray(row)) },
    statusLabel(value) { return { 1: '进行中', 2: '待评分', 3: '已评分', 4: '评分失败', 5: '已放弃' }[value] || '未记录' },
    formatDuration(sec) {
      return sec == null ? '未记录' : `${Math.floor(sec / 60)}分${sec % 60}秒`
    },
    speaker(role) {
      return role === 'user' ? '训练人员' + (this.record.userName ? ' · ' + this.record.userName : '') : role === 'assistant' ? 'AI 客户' : '其他消息'
    },
    open(row) {
      this.sessionId = row.sessionId || row.id
      this.activeTab = 'conversation'
      this.referencedIds = []
      this.visible = true
      return this.load()
    },
    clear() {
      this.requestSequence++
      this.detail = null
      this.error = ''
      this.loading = false
      this.referencedIds = []
      this.scrollToTop()
    },
    load() {
      const sessionId = this.sessionId
      const sequence = ++this.requestSequence
      this.detail = null
      this.error = ''
      this.loading = true
      this.referencedIds = []
      if (sessionId == null || sessionId === '') {
        this.error = '记录编号缺失，请刷新列表后重试。'
        this.loading = false
        return Promise.resolve()
      }
      return recordApi.fetchDetail(sessionId).then(res => {
        if (sequence !== this.requestSequence || !this.visible) return
        const data = res && res.data
        let messages = data && data.transcript && data.transcript.messages
        if (typeof messages === 'string') {
          try { messages = JSON.parse(messages) } catch (_) { /* 非法结构保留为失败，不伪装空记录 */ }
        }
        if (!data || !data.record || String(data.record.sessionId) !== String(sessionId) ||
          !data.transcript || !Array.isArray(messages) || !data.evaluation ||
          !['in_progress', 'evaluating', 'failed', 'done', 'abandoned'].includes(data.evaluation.state)) {
          throw new Error('训练记录详情格式异常，请重试。')
        }
        this.detail = { ...data, transcript: { ...data.transcript, messages } }
        this.activeTab = data.evaluation.state === 'done' ? 'evaluation' : 'conversation'
        this.$nextTick(() => this.scrollToTop())
      }).catch(() => {
        if (sequence !== this.requestSequence || !this.visible) return
        this.error = '训练记录加载失败，请重新加载；如仍失败，请联系管理员核对访问权限和服务状态。'
      }).finally(() => {
        if (sequence === this.requestSequence) this.loading = false
      })
    },
    scrollToTop() {
      if (this.$refs.detailBody) this.$refs.detailBody.scrollTop = 0
      if (this.$refs.conversation) this.$refs.conversation.scrollTop = 0
    },
    isReferenced(id) { return id != null && this.referencedIds.includes(String(id)) },
    showConversation(ids = []) {
      this.referencedIds = toArray(ids).map(String)
      this.activeTab = 'conversation'
      this.$nextTick(() => {
        const container = this.$refs.conversation
        if (!container) return
        this.scrollToTop()
        const message = Array.from(container.querySelectorAll('[data-message-id]')).find(node => this.referencedIds.includes(node.getAttribute('data-message-id')))
        if (message) container.scrollTop = message.offsetTop - container.offsetTop
        else container.scrollTop = 0
      })
    }
  }
}
</script>

<style scoped>
.record-detail /deep/ .el-dialog { max-width: calc(100vw - 32px); }
.record-detail /deep/ .el-dialog__body { padding-top: 10px; padding-bottom: 10px; }
.detail-body { min-height: 180px; max-height: 73vh; overflow-y: auto; padding-right: 6px; }
.detail-state { min-height: 180px; padding: 36px 16px; text-align: center; color: #606266; }
.retry-button { margin-top: 20px; }
.record-summary { white-space: pre-wrap; overflow-wrap: anywhere; }
.total-score { color: #167b70; }
.record-notice { margin-top: 16px; }
.record-notice /deep/ .el-alert__title { white-space: pre-wrap; overflow-wrap: anywhere; }
.detail-tabs { margin-top: 12px; }
.detail-tab-nav { display: flex; gap: 28px; border-bottom: 1px solid #e4e7ed; margin-bottom: 14px; }
.detail-tab-nav button { border: none; background: transparent; color: #606266; font-size: 14px; padding: 10px 0; cursor: pointer; }
.detail-tab-nav button.active { color: #267a5c; font-weight: 600; border-bottom: 3px solid #65b596; }
.section-hint { color: #909399; font-size: 13px; line-height: 1.8; white-space: pre-wrap; overflow-wrap: anywhere; }
.conversation-list { max-height: 49vh; min-height: 120px; overflow-y: auto; position: relative; padding: 4px; }
.conversation-message { margin: 12px 40px 16px 0; padding: 14px 16px; background: #f5f7fa; border: 1px solid #e4e7ed; border-radius: 6px; }
.message-user { margin-left: 40px; margin-right: 0; background: #f0f9f6; }
.message-highlight { border-color: #36a186; box-shadow: 0 0 0 2px #d8eee5; }
.message-meta { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; font-size: 12px; color: #909399; }
.message-meta strong { color: #43645c; overflow-wrap: anywhere; }
.message-meta time { margin-left: auto; }
.conversation-message p { margin: 10px 0 0; line-height: 1.9; white-space: pre-wrap; overflow-wrap: anywhere; }
.detail-empty { padding: 32px 16px; text-align: center; color: #909399; line-height: 1.8; white-space: pre-wrap; overflow-wrap: anywhere; }
@media (max-width: 760px) {
  .record-detail /deep/ .el-descriptions-item__label { min-width: 65px; }
  .record-detail /deep/ .el-descriptions-item__content { min-width: 85px; }
  .record-summary { overflow-x: auto; }
  .conversation-message { margin-right: 14px; }
  .message-user { margin-left: 14px; margin-right: 0; }
}
</style>
